import { useMemo } from "react";

import { capitalize } from "lodash";
import { useHistory } from "react-router-dom";

import {
  Button,
  Icon,
  Link,
  type TableFilterOption,
  toastError,
  toastSuccess,
  useDisclosure,
} from "@alpacahq/ui/old";
import {
  Table,
  type TableColumn,
  useDataGrid,
  useDataGridPersistence,
  useTableFilters,
} from "@alpacahq/ui/old/datagrid";

import { SymbolLink } from "~/v2/components/SymbolLink";
import { ORDERS_POLLING_INTERVAL } from "~/v2/constants";
import { titleCase } from "~/v2/helpers";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { useGetOrders, useLock } from "../../../api/hooks";
import { cancelOrder, type Order } from "../../../api/rest/account";
import {
  getPrettyAmount,
  getPrettyDate,
  getPrettyPrice,
  getQuoteCurrency,
} from "../../../utils/formatting";
import { CancelOrdersModal } from "./CancelOrdersModal";
import { getMultiLegOrderLabel } from "./OrdersTable.utils";

const PAGE_SIZES = [5, 10, 20, 50, 100, 500];

const MAX_PRICE_DECIMALS_CRYPTO = 9;
const DEFAULT_MAX_PRICE_DECIMALS = 6;

const priceDecimals = (assetClass: string) =>
  assetClass === "crypto"
    ? MAX_PRICE_DECIMALS_CRYPTO
    : DEFAULT_MAX_PRICE_DECIMALS;

const orderLegs = (legs: Order["legs"]) => (Array.isArray(legs) ? legs : []);

const ORDER_STATUSES = [
  "open",
  "closed",
  "new",
  "accepted",
  "filled",
  "expired",
  "canceled",
  "replaced",
  "rejected",
  "suspended",
  "partially_filled",
  "pending_cancel",
  "pending_new",
  "pending_replace",
  "pending_review",
];

const ORDER_STATUS_OPTIONS: TableFilterOption[] = ORDER_STATUSES.map((s) => ({
  label: titleCase(s),
  value: s,
}));

type Props = {
  limit?: number;
  symbol?: string;
  hideFilters?: boolean;
  defaultPageSize?: number;
  hidePageSize?: boolean;
  defaultVisibleColumns?: string[];
  persistenceKey?: string;
};
export const OrdersTable = ({
  limit,
  symbol,
  hideFilters,
  hidePageSize,
  defaultPageSize = 50,
  defaultVisibleColumns,
  persistenceKey,
}: Props) => {
  const lock = useLock();
  const history = useHistory();
  const disclosure = useDisclosure();

  const { product, currentAccount } = useAccountContext();

  const columns: TableColumn<Order>[] = useMemo(
    () => [
      {
        name: "ID",
        key: "id",
        disableSort: true,
        cellRenderer: ({ id }) => (
          <Link
            onClick={() =>
              history.push(
                Path.format(Path.ROUTE_ORDER_DETAIL, { orderId: id }),
              )
            }
          >
            {id}
          </Link>
        ),
      },
      {
        name: "Asset",
        key: "symbol",
        disableSort: true,
        cellRenderer: ({ id, symbol, order_class, legs }, _, parentOrder) => {
          const multiLegOrderLabel = getMultiLegOrderLabel(
            order_class,
            legs,
            Boolean(parentOrder),
          );
          if (multiLegOrderLabel) {
            return (
              <div className="flex">
                <div className="w-44">
                  <Link
                    onClick={() => {
                      history.push(
                        Path.format(Path.ROUTE_ORDER_DETAIL, { orderId: id }),
                      );
                    }}
                    className="font-medium"
                  >
                    {multiLegOrderLabel}
                  </Link>
                </div>
              </div>
            );
          }
          return <SymbolLink symbol={symbol} />;
        },
      },
      {
        name: "Order Type",
        key: "order_type",
        disableSort: true,
        cellRenderer: ({
          type,
          limit_price,
          stop_price,
          symbol,
          asset_class,
        }) => {
          // An absent `type` falls through to the `""` case below ("-").
          const orderType = type ?? "";
          const typeFormatted = capitalize(orderType.replaceAll("_", " "));

          switch (orderType.trim()) {
            case "stop": {
              const stopPriceFormatted = getPrettyPrice(
                stop_price ?? "",
                symbol,
                asset_class,
                2,
                priceDecimals(asset_class),
              );
              return `${typeFormatted} @ ${stopPriceFormatted}`;
            }
            case "limit":
            case "stop_limit":
            case "mleg": {
              const limitPriceFormatted = getPrettyPrice(
                limit_price ?? "",
                symbol,
                asset_class,
                2,
                priceDecimals(asset_class),
              );
              return `${typeFormatted} @ ${limitPriceFormatted}`;
            }
            case "":
              return "-";
            default:
              return typeFormatted;
          }
        },
      },
      {
        name: "Type",
        key: "type",
        disableSort: true,
      },
      {
        name: "Side",
        key: "side",
        disableSort: true,
        cellRenderer: ({ side, order_class }, _, parentOrder) =>
          order_class === "mleg" && !parentOrder ? "-" : side,
      },
      {
        name: "Position Intent",
        key: "position_intent",
        disableSort: true,
      },
      {
        name: "Qty",
        key: "qty",
        alignRight: true,
        getValue: ({ qty }) => parseFloat(qty ?? ""),
        cellRenderer: ({ qty }) => getPrettyAmount(qty ?? null, 2, 8),
      },
      {
        name: "Ratio Qty",
        key: "ratio_qty",
        alignRight: true,
        disableSort: true,
        cellRenderer: ({ ratio_qty }) =>
          getPrettyAmount(ratio_qty ?? null, 2, 8),
      },
      {
        name: "Filled Qty",
        key: "filled_qty",
        alignRight: true,
        getValue: ({ filled_qty }) => parseFloat(filled_qty),
        cellRenderer: ({ filled_qty }) =>
          getPrettyAmount(filled_qty ?? null, 2, 8),
      },
      {
        name: "Currency",
        key: "currency",
        disableSort: true,
        getValue: ({ symbol, asset_class }, parentOrder) =>
          parentOrder ? "-" : getQuoteCurrency(symbol, asset_class),
      },
      {
        name: "Avg. Fill Price",
        key: "filled_avg_price",
        alignRight: true,
        getValue: ({ filled_avg_price }) => parseFloat(filled_avg_price ?? ""),
        cellRenderer: ({ filled_avg_price, asset_class }) =>
          getPrettyAmount(
            filled_avg_price ?? null,
            2,
            priceDecimals(asset_class),
          ),
      },
      {
        name: "Limit Price",
        key: "limit_price",
        alignRight: true,
        getValue: ({ limit_price }) => parseFloat(limit_price ?? ""),
        cellRenderer: ({ limit_price, asset_class }) =>
          getPrettyAmount(limit_price, 2, priceDecimals(asset_class)),
      },
      {
        name: "Stop Price",
        key: "stop_price",
        alignRight: true,
        getValue: ({ stop_price }) => parseFloat(stop_price ?? ""),
        cellRenderer: ({ stop_price, asset_class }) =>
          getPrettyAmount(stop_price, 2, priceDecimals(asset_class)),
      },
      {
        name: "Notional",
        key: "notional",
        alignRight: true,
        getValue: ({ notional }) => parseFloat(notional ?? ""),
        cellRenderer: ({ notional, asset_class }) =>
          getPrettyAmount(notional, 2, priceDecimals(asset_class)),
      },
      {
        name: "Total Amount",
        key: "amount",
        alignRight: true,
        getValue: ({ filled_qty, filled_avg_price }) =>
          parseFloat(filled_qty) * parseFloat(filled_avg_price ?? ""),
        cellRenderer: ({ asset_class }, value) =>
          getPrettyAmount(value as number, 2, priceDecimals(asset_class)),
      },
      {
        name: "Status",
        key: "status",
        disableSort: true,
      },
      {
        name: "Source",
        key: "source",
        disableSort: true,
        cellRenderer: ({ source }, _, parentOrder) =>
          source || parentOrder?.source || "-",
      },
      {
        name: "Algo",
        key: "algorithm",
        cellRenderer: ({ advanced_instructions }) =>
          advanced_instructions?.algorithm || "-",
      },
      {
        name: "Submitted At",
        key: "submitted_at",
        cellRenderer: ({ submitted_at }) => getPrettyDate(submitted_at),
      },
      {
        name: "Filled At",
        key: "filled_at",
        cellRenderer: ({ filled_at }) => getPrettyDate(filled_at),
      },
      {
        name: "Canceled At",
        key: "canceled_at",
        cellRenderer: ({ canceled_at }) => getPrettyDate(canceled_at),
      },
      {
        name: "Replaced At",
        key: "replaced_at",
        cellRenderer: ({ replaced_at }) => getPrettyDate(replaced_at),
      },
      {
        name: "Expires At",
        key: "expires_at",
        cellRenderer: ({ expires_at }) => getPrettyDate(expires_at),
      },
    ],
    [history],
  );

  const filters = useTableFilters({
    enabled: !hideFilters,
    columns,
    rows: useMemo(() => [], []),
    defaultActiveFilters: [{ filterKey: "status", value: "open" }],
    filters: [
      {
        key: "status",
        label: "Status",
        options: ORDER_STATUS_OPTIONS,
      },
      {
        key: "side",
        label: "Side",
        options: [
          { label: "Buy", value: "buy" },
          { label: "Sell", value: "sell" },
          { label: "Sell Short", value: "sell_short" },
        ],
      },
    ],
  });

  const statusFilter =
    (filters.activeFilters?.find(({ filterKey }) => filterKey === "status")
      ?.value as "open" | "closed" | undefined) ?? "all";
  const sideFilter = filters.activeFilters?.find(
    ({ filterKey }) => filterKey === "side",
  )?.value as "buy" | "sell" | "sell_short" | undefined;

  const getOrdersQuery = useGetOrders({
    accountID: currentAccount?.id || "",
    product,
    status: statusFilter,
    side: sideFilter,
    limit,
    options: {
      enabled: !!currentAccount?.id && !!product,
      refetchInterval: ORDERS_POLLING_INTERVAL,
    },
  });

  const { refetch, isLoading } = getOrdersQuery;

  /**
   * This manual filtering is ultimately caused by a disconnect between the order
   * ticket dialog and this orders table.
   *
   * Filtering the orders query by symbol at the API level means that the react
   * query that gets refreshed by the order ticket is not the same one that is
   * used by this table, causing newly placed orders to not be refreshed.
   *
   * Additionally, we need a way to query orders by underlying symbol, otherwise
   * options orders do not show up properly when filtering by symbol via the API.
   */
  // TODO: Improve order API symbol filtering and order ticket <> orders table synchronization.
  const orders = useMemo(() => {
    if (!symbol) {
      return getOrdersQuery.orders;
    }
    const matcher = new RegExp(`^${symbol}(\\d{6}[CP]\\d{8})?$`, "i");
    return getOrdersQuery.orders.filter(
      (o) =>
        (o.symbol ?? "").match(matcher) ||
        orderLegs(o.legs).some((leg) => (leg.symbol ?? "").match(matcher)),
    );
  }, [getOrdersQuery.orders, symbol]);

  const tableProps = useDataGrid({
    rows: orders,
    columns,
    getNestedRows: ({ legs }) => orderLegs(legs),
    pagination: {
      pageSizeOptions: PAGE_SIZES,
      defaultPageSize,
      hidePageSize,
    },
    columnVisibility: {
      defaultVisibleColumns: defaultVisibleColumns ?? [
        "symbol",
        "qty",
        "filled_qty",
        "side",
        "order_type",
        "filled_avg_price",
        "status",
        "source",
        "submitted_at",
        "filled_at",
        "expires_at",
        "action",
      ],
    },
    search: {
      searchPlaceholder: "Search...",
      searchColumns: ["symbol", "type", "side", "status"],
    },
    rowSelection: {
      rowSelectionKey: "id",
    },
    sort: true,
  });

  const persistence = useDataGridPersistence({
    enabled: !!persistenceKey,
    columns,
    columnVisibility: tableProps.columnVisibility,
    filters,
    pagination: tableProps.pagination,
    persistence: {
      key: persistenceKey ?? "",
    },
  });

  const onConfirm = async () => {
    // prevent multiple clicks
    lock.lock();

    // do nothing if locked or no positions selected or account context isn't ready.
    if (
      !product ||
      !currentAccount ||
      lock.locked ||
      !tableProps.rowSelection ||
      !tableProps.rowSelection.selectedRows.size
    ) {
      return;
    }

    const { selectedRows, onSelectRow } = tableProps.rowSelection;

    const canceledOrders: Order[] = [];
    const accountID = currentAccount.id;

    for (const order of Array.from(selectedRows)) {
      try {
        await cancelOrder({ accountID, product, orderID: order.id });
        canceledOrders.push(order);
      } catch (error) {
        console.error(`Error cancelling order ${order.id}:`, error);
      }
    }

    const notCanceledCount = selectedRows.size - canceledOrders.length;

    if (notCanceledCount > 0) {
      toastError(
        `Error cancelling order: Failed to cancel ${notCanceledCount} orders.`,
      );
    } else {
      toastSuccess(`Successfully cancelled ${selectedRows.size} orders.`);
    }

    for (const canceledOrder of canceledOrders) {
      onSelectRow(canceledOrder);
    }

    try {
      // Refetch orders
      await refetch();
    } catch (error) {
      toastError(
        `Error refetching orders: ${(error as { message?: string })?.message || "unknown"}`,
        {},
      );
    } finally {
      // Dismiss modal
      disclosure.onClose();

      // Ready for next click
      lock.unlock();
    }
  };

  const selectedRowsCount = tableProps.rowSelection?.selectedRows.size ?? 0;

  return (
    <>
      <Table
        {...tableProps}
        isLoading={isLoading}
        filters={filters}
        persistence={persistence}
        emptyMessage={
          isLoading
            ? "Loading..."
            : "No orders. Place a trade via the dashboard or the API to see this table populate."
        }
        tools={[
          <Button
            key="cancel-selected"
            onClick={disclosure.onOpen}
            disabled={selectedRowsCount === 0 || lock.locked}
          >
            Cancel {selectedRowsCount} selected
          </Button>,
        ]}
        getRowActions={({ id }: Order, parentRow) =>
          !parentRow && (
            <Button
              variant="ghost"
              onClick={() =>
                history.push(
                  Path.format(Path.ROUTE_ORDER_DETAIL, { orderId: id }),
                )
              }
            >
              <Icon name="ArrowTopRightOnSquare" className="text-gray-600" />
            </Button>
          )
        }
      />
      <CancelOrdersModal
        isOpen={disclosure.isOpen}
        isLocked={lock.locked}
        onConfirm={onConfirm}
        onClose={disclosure.onClose}
        selectedRowsCount={selectedRowsCount}
      />
    </>
  );
};
